import Api from 'api';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/checkmark.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit2.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import Button from 'atoms/Button';
import Container from 'atoms/Container';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { openModal } from 'store/layoutSlice';

import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import ContentBox from 'atoms/ContentBox';
import Grid from 'atoms/Grid';
import ListHeader from 'atoms/ListHeader/ListHeader';
import Loader from 'layout/molecules/Loader';

type Automation = { id: string; name: string; status: 'OFF' | 'ON' };

export const AutomationConfigPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [automations, setAutomations] = useState<Automation[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        Api.defaultApi()
            .fetchAutomations()
            .then((response) => {
                setAutomations(response as Automation[]);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, []);

    const handleAutomationDeleted = useCallback((event: Event) => {
        const { id } = (event as CustomEvent).detail;
        setAutomations((prev) => prev.filter((a) => a.id !== id));
    }, []);

    useEffect(() => {
        window.addEventListener('automationDeleted', handleAutomationDeleted);
        return () => {
            window.removeEventListener('automationDeleted', handleAutomationDeleted);
        };
    }, [handleAutomationDeleted]);

    const handleDeleteClick = (automation: Automation) => {
        dispatch(
            openModal({
                name: 'deleteAutomation',
                props: { automationId: automation.id, automationName: automation.name },
            })
        );
    };

    return (
        <>
            <Container size="s">
                <ListHeader
                    text="Configure Automation"
                    aside={
                        <>
                            <Button
                                size="m"
                                theme="plain"
                                to="/automation/configure/create"
                            >
                                <PlusIcon /> <p>Add</p>
                            </Button>
                            {automations.length > 0 && (
                                <Button size="m" theme="plain" to="/automation">
                                    <CheckmarkIcon /> <p>Done</p>
                                </Button>
                            )}
                        </>
                    }
                />
            </Container>
            <Container size="s">
                <Grid columns={1} gap={12}>
                    {isLoading ? (
                        <ContentBox>
                            <Loader />
                        </ContentBox>
                    ) : (
                        <>
                            {automations.length === 0 && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        background: 'var(--box-background)',
                                        borderRadius: 'var(--radius)',
                                        border: '1px solid var(--grey400)',
                                        marginBottom: '24px',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: '50%',
                                            background:
                                                'linear-gradient(135deg, #9B59B6, #8E44AD)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginBottom: '24px',
                                            fontSize: '32px',
                                        }}
                                    >
                                        ⚙️
                                    </div>
                                    <h3
                                        style={{
                                            margin: '0 0 12px 0',
                                            fontSize: '20px',
                                            fontWeight: '600',
                                            color: 'var(--text)',
                                        }}
                                    >
                                        No automations yet
                                    </h3>
                                    <p
                                        style={{
                                            margin: '0 0 24px 0',
                                            fontSize: '16px',
                                            lineHeight: '1.5',
                                            color: 'var(--text)',
                                            opacity: 0.7,
                                            maxWidth: '400px',
                                        }}
                                    >
                                        Create your first automation to handle
                                        recurring tasks automatically.
                                    </p>
                                </div>
                            )}

                            {automations.length > 0 &&
                                automations.map((automation, index) => (
                                    <ContentBox key={index}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '0',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: '15px',
                                                    fontWeight: '500',
                                                    color: 'var(--text)',
                                                    flex: 1,
                                                    minWidth: 0,
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {automation.name}
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    background:
                                                        automation.status ===
                                                        'ON'
                                                            ? 'rgba(34, 197, 94, 0.1)'
                                                            : 'rgba(156, 163, 175, 0.1)',
                                                    color:
                                                        automation.status ===
                                                        'ON'
                                                            ? '#22c55e'
                                                            : '#9ca3af',
                                                    padding: '3px 6px',
                                                    borderRadius: '8px',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '4px',
                                                        height: '4px',
                                                        borderRadius: '50%',
                                                        background:
                                                            automation.status ===
                                                            'ON'
                                                                ? '#22c55e'
                                                                : '#9ca3af',
                                                    }}
                                                ></div>
                                                {automation.status}
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    marginLeft: '16px',
                                                }}
                                            >
                                                {false && (
                                                    <Button
                                                        size="s"
                                                        theme="blank"
                                                        shape="square"
                                                        /*to={`/automation/${automation.id}/edit`}*/
                                                        style={{
                                                            padding: '4px 8px',
                                                            fontSize: '11px',
                                                            whiteSpace:
                                                                'nowrap',
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </Button>
                                                )}
                                                <Button
                                                    size="s"
                                                    theme="blank"
                                                    shape="square"
                                                    onClick={() =>
                                                        handleDeleteClick(
                                                            automation
                                                        )
                                                    }
                                                    style={{
                                                        padding: '4px 8px',
                                                        fontSize: '11px',
                                                        whiteSpace: 'nowrap',
                                                        color: '#ef4444',
                                                    }}
                                                >
                                                    <TrashIcon />
                                                </Button>
                                            </div>
                                        </div>
                                    </ContentBox>
                                ))}
                        </>
                    )}
                    <Button
                        size="m"
                        theme="primary"
                        to="/automation/configure/create"
                        style={{
                            fontSize: '14px',
                            padding: '12px 24px',
                        }}
                    >
                        <PlusIcon /> <span>New automation</span>
                    </Button>
                    <Button
                        size="m"
                        theme="plain"
                        to="/automation"
                        style={{
                            fontSize: '14px',
                            padding: '12px 24px',
                        }}
                    >
                        Go Back
                    </Button>
                </Grid>
            </Container>
        </>
    );
};
